import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import MediaQuery from 'react-responsive'
import shortId from 'shortid'
import posthog from 'posthog-js'
import Button from '../button/button'
import Square from './square'
import Keyboard from './keyboard'
import MobilePingButton from './mobile-ping-button'
import './crossword.scss'

const VALID_KEYS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LONGPRESS_DELAY = 600

export class Crossword extends Component {
    static propTypes = {
        across: PropTypes.object,
        down: PropTypes.object,
        width: PropTypes.number,
        height: PropTypes.number,
        board: PropTypes.string,
        solution: PropTypes.string,
        title: PropTypes.string,
        showClues: PropTypes.bool,
        showCheck: PropTypes.bool,
        updateLetter: PropTypes.func,
        users: PropTypes.shape({
            color: PropTypes.string,
            row: PropTypes.number,
            col: PropTypes.number,
        }),
        me: PropTypes.shape({
            color: PropTypes.string,
        }),
        settings: PropTypes.object,
        clues: PropTypes.shape({
            down: PropTypes.object,
            across: PropTypes.object,
        }),
    }

    static defaultProps = {
        showClues: true,
        showCheck: true,
        settings: {},
    }

    constructor (props) {
        super(props)

        const board = Array.from(Array(props.width), () => Array(props.height).fill(''))

        this.state = {
            selectedSquare: {},
            board,
            checking: props.settings.check,
            direction: 'across',
            highlights: [...board],
            clueSquare: {},
            pingKeys: [...board],
            finishedClues: new Set(),
        }

        this._isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

        this._squares = {}
        this._startTouchTime = 0

        document.addEventListener('keydown', this.onKeyDown)
        // document.addEventListener('dblclick', this.toggleDirection)
        // document.addEventListener('touchstart', this.touchStart)
        // document.addEventListener('touchend', this.touchEnd)
    }

    componentWillUnmount = () => {
        document.removeEventListener('keydown', this.onKeyDown)
        // document.removeEventListener('dblclick', this.toggleDirection)
        // document.removeEventListener('touchstart', this.touchStart)
        // document.removeEventListener('touchend', this.touchEnd)
        if (this._touchInterval) {
            clearInterval(this._touchInterval)
        }
    }

    setInitialPosition = () => {
        const filled = this.isFilled(0, 0)
        this.updateSelectedSquare({ row: 0, col: 0 }, filled)
        if (filled) {
            const nextSquare = this.getNextSquare(true)
            this.updateSelectedSquare(nextSquare)
        }
    }

    touchStart = () => {
        this._startTouchTime = Date.now()
        this._touchInterval = setInterval(() => {
            if (Date.now() - this._startTouchTime >= LONGPRESS_DELAY) {
                this.toggleDirection()
                this._startTouchTime = Date.now()
            }
        }, 100)
    }

    touchEnd = () => {
        if (this._touchInterval) {
            clearInterval(this._touchInterval)
        }
    }

    onClickClue = () => {
        posthog.capture('clue_click')
        this.toggleDirection()
    }

    toggleDirection = () => {
        const { direction } = this.state
        this.setState({ direction: direction === 'across' ? 'down' : 'across' }, () => {
            this.updateHighlights()
        })
    }

    componentDidUpdate = (prevProps, prevState) => {
        const cluesToCheck = []
        const { board, width, height } = this.props
        if (board !== prevProps.board) {
            // const newBoard = Array.from(Array(width), () => Array(height).fill(''))
            const newBoard = []
            const newPingKeys = []
            for (let i=0;i<height;++i) {
                const row = []
                const pingRow = []
                for (let j=0;j<width;++j) {
                    const item = board[(i*width)+j]
                    if (item === '.' || item === '-') {
                        row.push('')
                    } else {
                        row.push(item)
                        cluesToCheck.push({ x: i, y: j })
                    }
                    pingRow.push(shortId.generate())
                }
                newBoard.push(row)
                newPingKeys.push(pingRow)
            }
            this.setState({ board: newBoard, pingKeys: newPingKeys })
        }
    }

    onVirtualKeyDown = (key) => {
        const { updateLetter } = this.props
        const { selectedSquare, board, clueSquare, direction } = this.state

        if (isNaN(parseInt(selectedSquare.row)) || isNaN(parseInt(selectedSquare.col))) {
            return
        }

        if (this.isFilled(selectedSquare.row, selectedSquare.col)) {
            return
        }

        key = key.toUpperCase()
        if (key === '{BACKSPACE}' || key === ' ' || key === '' || !key) {
            // no-op
        } else if (!VALID_KEYS.split('').includes(key)) {
            return
        }

        const newBoard = { ...board }

        let text = key
        if (key === '{BACKSPACE}' || key === ' ' || !key) {
            text = ''
        }

        newBoard[selectedSquare.row][selectedSquare.col] = text
        this.setState({ board: newBoard })
        this.updateFinishedClues(clueSquare, direction)
        updateLetter(selectedSquare.row, selectedSquare.col, text)
        const newSquare = key === '{BACKSPACE}' ? this.getPrevSquare() : this.getNextSquare()
        this.updateSelectedSquare(newSquare)
    }

    onKeyDown = (e) => {
        const { updateLetter } = this.props
        const { selectedSquare, board, direction, clueSquare } = this.state
        let key = e.key.toUpperCase()
        if (e.keyCode === 229) {
            return
        }
        if (isNaN(parseInt(selectedSquare.row)) || isNaN(parseInt(selectedSquare.col))) {
            return
        }

        posthog.capture('key_press', { key })

        if (key === 'ARROWUP') {
            e.preventDefault()
            this.moveUp()
            return
        } else if (key === 'ARROWDOWN' || key === 'ENTER') {
            e.preventDefault()
            this.moveDown()
            return
        } else if (key === 'ARROWLEFT') {
            e.preventDefault()
            this.moveLeft()
            return
        } else if (key === 'ARROWRIGHT') {
            e.preventDefault()
            this.moveRight()
            return
        } else if (key === 'TAB') {
            e.preventDefault()
            // this.toggleDirection()
            this.moveNext(e.shiftKey)
            return
        } else if (key === ' ') {
            e.preventDefault()
            this.toggleDirection()
            return
        } else if (key === 'BACKSPACE' || key === '') {
            // no-op
        } else if (!VALID_KEYS.split('').includes(key)) {
            return
        }
        e.preventDefault()
        const newBoard = { ...board }

        let text = key
        if (key === 'BACKSPACE') {
            text = ''
        }

        if (this.isFilled(selectedSquare.row, selectedSquare.col)) {
            return
        }

        newBoard[selectedSquare.row][selectedSquare.col] = text
        this.setState({ board: newBoard })
        this.updateFinishedClues(clueSquare, direction)
        updateLetter(selectedSquare.row, selectedSquare.col, text)
        const newSquare = key === 'BACKSPACE' ? this.getPrevSquare() : this.getNextSquare()
        this.updateSelectedSquare(newSquare)
    }

    updateFinishedClues = ({ row, col }, direction) => {
        // todo
        return null
        const { finishedClues } = this.state
        const clues = this.props[direction]
        let clueId
        Object.keys(clues).forEach(clueKey => {
            const clue = clues[clueKey]
            if (clue.row === row && clue.col === col) {
                clueId = clueKey
            }
        })
        if (this.isLineComplete(row, col, direction)) {
            const newFinishedClues = new Set(finishedClues)
            newFinishedClues.add(clueId)
            this.setState({ finishedClues: newFinishedClues })
        }
    }

    onSquareClick (e, rowIdx, colIdx) {
        const { row, col } = this.state.selectedSquare
        if (this.isFilled(rowIdx, colIdx)) {
            return
        }
        posthog.capture('square_click', { row: rowIdx, col: colIdx })
        // if (this._input) {
        //     this._input.style.top = `${25 * rowIdx}px`
        //     this._input.focus()
        // }
        if (row === rowIdx && col === colIdx) {
            this.toggleDirection()
            return
        }
        this.updateSelectedSquare({ row: rowIdx, col: colIdx })
    }

    isFilled = (x, y) => {
        const { board, width } = this.props
        return board[(x*width)+y] === '.'
    }

    isSquareComplete = (x, y) => {
        const { board, width } = this.props
        return board[(x*width)+y] !== ' '
    }

    isLineComplete = (x, y, direction='across') => {
        const { height, width } = this.props
        if (x < 0 || x >= width) {
            return false
        }
        if (y < 0 || y >= height) {
            return false
        }

        let x0 = x
        let y0 = y

        let done = this.isFilled(x0, y0)
        if (done) {
            return false
        }

        if (!this.isSquareComplete(x0, y0)) {
            return false
        }

        if (direction === 'across') {
            // left
            done = false
            while (!done) {
                x0 -= 1
                if (!this.isSquareComplete(x0, y0)) {
                    return false
                }
                done = x === 0 || this.isFilled(x0, y0)
            }
            x0 = x
            done = false
            // right
            while (!done) {
                x0 += 1
                if (!this.isSquareComplete(x0, y0)) {
                    return false
                }
                done = x === width - 1 || this.isFilled(x0, y0)
            }
        } else {
            // up
            x0 = x
            y0 = y
            done = false
            while (!done) {
                y0 -= 1
                if (!this.isSquareComplete(x0, y0)) {
                    return false
                }
                done = y === 0 || this.isFilled(x0, y0)
            }
            done = false
            y0 = y
            // down
            while (!done) {
                y0 += 1
                if (!this.isSquareComplete(x0, y0)) {
                    return false
                }
                done = y === height - 1 || this.isFilled(x0, y0)
            }
        }

        return true
    }

    moveNext = (reverse = false) => {
        const { clueSquare, direction } = this.state
        const { down, across } = this.props
        const clues = direction === 'across' ? across : down

        let nextClue
        let curClueKey
        if (!reverse) {
            Object.keys(clues).forEach(key => {
                const clue = clues[key]
                if (curClueKey) {
                    nextClue = clue
                    curClueKey = null
                    return
                }
                if (clue.row === clueSquare.row && clue.col === clueSquare.col) {
                    curClueKey = key
                }
            })
        } else {
            const keys = Object.keys(clues)
            for (let i=keys.length-1;i>=0;--i) {
                const key = keys[i]
                const clue = clues[key]
                if (curClueKey) {
                    nextClue = clue
                    curClueKey = null
                    break
                }
                if (clue.row === clueSquare.row && clue.col === clueSquare.col) {
                    curClueKey = key
                }
            }
        }

        console.log('[moveNext] nextClue', curClueKey, nextClue)
        if (!nextClue) {
            const nextDirection = direction === 'across' ? 'down' : 'across'
            let nextClue
            if (!reverse) {
                nextClue = Object.values(this.props[nextDirection])[0]
            } else {
                const keys = Object.keys(this.props[nextDirection])
                nextClue = this.props[nextDirection][keys[keys.length - 1]]
            }
            this.updateSelectedSquare({ row: nextClue.row, col: nextClue.col })
            this.setState({ direction: nextDirection })
            return
        }
        this.updateSelectedSquare({ row: nextClue.row, col: nextClue.col })
    }

    moveUp = () => {
        const { height } = this.props
        const { selectedSquare, direction } = this.state
        const newSquare = { ...selectedSquare }
        if (direction === 'across') {
            this.toggleDirection()
            return
        }
        const goUp = () => {
            newSquare.row--
            if (newSquare.row < 0) {
                newSquare.row = height - 1
            }
        }
        goUp()
        while(this.isFilled(newSquare.row, newSquare.col)) {
            goUp()
        }
        this.updateSelectedSquare(newSquare)
    }

    moveDown = () => {
        const { height } = this.props
        const { selectedSquare, direction } = this.state
        if (direction === 'across') {
            this.toggleDirection()
            return
        }
        const newSquare = { ...selectedSquare }
        const goDown = () => {
            newSquare.row++
            if (newSquare.row >= height) {
                newSquare.row = 0
            }
        }
        goDown()
        while(this.isFilled(newSquare.row, newSquare.col)) {
            goDown()
        }
        this.updateSelectedSquare(newSquare)
    }

    moveRight = () => {
        const { width } = this.props
        const { selectedSquare, direction } = this.state
        if (direction === 'down') {
            this.toggleDirection()
            return
        }
        const newSquare = { ...selectedSquare }
        const goUp = () => {
            newSquare.col++
            if (newSquare.col >= width) {
                newSquare.col = 0
            }
        }
        goUp()
        while(this.isFilled(newSquare.row, newSquare.col)) {
            goUp()
        }
        this.updateSelectedSquare(newSquare)
    }

    moveLeft = () => {
        const { width } = this.props
        const { selectedSquare, direction } = this.state
        if (direction === 'down') {
            this.toggleDirection()
            return
        }
        const newSquare = { ...selectedSquare }
        const goDown = () => {
            newSquare.col--
            if (newSquare.col <= 0) {
                newSquare.col = width - 1
            }
        }
        goDown()
        while(this.isFilled(newSquare.row, newSquare.col)) {
            goDown()
        }
        this.updateSelectedSquare(newSquare)
    }

    getNextSquare = (force=false) => {
        const { width, height } = this.props
        const { selectedSquare, direction } = this.state
        const newSquare = { ...selectedSquare }

        const nextSquare = () => {
            if (direction === 'across') {
                newSquare.col++
                if (newSquare.col >= width) {
                    newSquare.col--
                    if (force) {
                        newSquare.col = 0
                        newSquare.row++
                    }
                }
                if (newSquare.row >= height && force) {
                    newSquare.row = 0
                }
            } else {
                newSquare.row++
                if (newSquare.row >= height) {
                    newSquare.row--
                    if (force) {
                        newSquare.row = 0
                        newSquare.col++
                    }
                }
                if (newSquare.col >= width && force) {
                    newSquare.col = 0
                }
            }
        }

        nextSquare()

        if (force) {
            while (this.isFilled(newSquare.row, newSquare.col)) {
                nextSquare()
            }
        } else {
            if (this.isFilled(newSquare.row, newSquare.col)) {
                return selectedSquare
            }
        }

        return newSquare
    }

    getPrevSquare = () => {
        const { width, height } = this.props
        const { selectedSquare, direction } = this.state
        const newSquare = { ...selectedSquare }

        const previousSquare = () => {
            if (direction === 'across') {
                newSquare.col--
                if (newSquare.col < 0) {
                    newSquare.col = width - 1
                    newSquare.row--
                }
                if (newSquare.row < 0) {
                    newSquare.row = height - 1
                }
            } else if (direction === 'down') {
                newSquare.row--
                if (newSquare.row < 0) {
                    newSquare.row = height - 1
                    newSquare.col--
                }
                if (newSquare.col < 0) {
                    newSquare.col = width - 1
                }
            }
        }

        previousSquare()
        while (this.isFilled(newSquare.row, newSquare.col)) {
            previousSquare()
        }
        return newSquare
    }

    updateHighlights () {
        const { width, height } = this.props
        const { selectedSquare, direction } = this.state
        const highlights = {}
        const clueSquare = { ...selectedSquare }

        if (direction === 'across') {
            highlights[selectedSquare.row] = {}
            clueSquare.row = selectedSquare.row
            let i = selectedSquare.col
            let done = false
            while (i >= 0 && !done) {
                if (!this.isFilled(selectedSquare.row, i)) {
                    highlights[selectedSquare.row][i] = true
                    clueSquare.col = i
                    i--
                } else {
                    done = true
                }
            }
            done = false
            i = selectedSquare.col
            while (i <= width && !done) {
                if (!this.isFilled(selectedSquare.row, i)) {
                    highlights[selectedSquare.row][i] = true
                    i++
                } else {
                    done = true
                }
            }
        } else if (direction === 'down') {
            let i = selectedSquare.row
            clueSquare.col = selectedSquare.col
            let done = false
            while (i >= 0 && !done) {
                if (!this.isFilled(i, selectedSquare.col)) {
                    if (!highlights[i]) {
                        highlights[i] = {}
                    }
                    highlights[i][selectedSquare.col] = true
                    clueSquare.row = i
                    i--
                } else {
                    done = true
                }
            }
            done = false
            i = selectedSquare.row
            while (i <= height && !done) {
                if (!this.isFilled(i, selectedSquare.col)) {
                    if (!highlights[i]) {
                        highlights[i] = {}
                    }
                    highlights[i][selectedSquare.col] = true
                    i++
                } else {
                    done = true
                }
            }
        }
        this.setState({ highlights, clueSquare })
    }

    updateSelectedSquare = (newSquare, update=true) => {
        const { updatePos } = this.props
        const { users={} } = this.state
        if (update) {
            updatePos(newSquare.row, newSquare.col)
        }
        this.setState({ users: {
            ...users,
            [-1]: {
                ...(users[-1] || {}),
                ...newSquare,
            },
        }})
        this.setState({ selectedSquare: newSquare }, () => {
            this.updateHighlights()
            // focus
            // const squareRef = this._squares[newSquare.row] && this._squares[newSquare.row][newSquare.col]
            // if (squareRef) {
            //     squareRef.focus()
            // }
        })
    }

    renderRow (row, rowIdx) {
        const { solution, width, users, emitPing, me={}, clues } = this.props
        const { down, across } = clues
        const { selectedSquare, board, checking, highlights, pingKeys } = this.state
        return (
            <div className='Row' key={`row=${rowIdx}`}>
                {row.map((filled, colIdx) => {
                    const idx = (rowIdx * width) + colIdx
                    const text = board[rowIdx] && board[rowIdx][colIdx]
                    const pingKey = pingKeys[rowIdx] && pingKeys[rowIdx][colIdx]
                    const mistake = checking && solution[idx].toUpperCase() !== text
                    const selected = selectedSquare.row === rowIdx && selectedSquare.col === colIdx
                    const user = Object.values(users).find(user => user.row === rowIdx && user.col === colIdx)
                    const onClick = (e) => this.onSquareClick(e, rowIdx, colIdx)
                    const downClue = down[rowIdx]?.[colIdx]
                    const acrossClue = across[rowIdx]?.[colIdx]
                    const clue = (downClue || acrossClue || '').toString()
                    return (
                        <MediaQuery maxWidth={1000} key={`cell-${rowIdx}-${colIdx}`}>
                            {mobile => {
                                const squareProps = {
                                    key: `square-${idx}`,
                                    mistake,
                                    text,
                                    user,
                                    highlighted: highlights[rowIdx] && highlights[rowIdx][colIdx],
                                    selected,
                                    onClick,
                                    filled,
                                    pingKey,
                                }
                                if (mobile) {
                                    const squareWidth = `calc(100% / ${width})`
                                    const squareHeight = '25px'
                                    squareProps.style = { width: squareWidth, height: squareHeight }
                                }
                                return (
                                    <Square
                                        {...squareProps}
                                        // ref={ref => {
                                        //     if (!this._squares[rowIdx]) {
                                        //         this._squares[rowIdx] = {}
                                        //     }
                                        //     this._squares[rowIdx][colIdx] = ref
                                        // }}
                                        clueNumber={clue}
                                        myColor={me.color}
                                        onPing={() => emitPing(rowIdx, colIdx)}
                                        onKeyDown={this.onKeyDown} />
                                )
                            }}
                        </MediaQuery>
                    )
                })}
            </div>
        )
    }

    renderBoard = () => {
        const { checking } = this.state
        const { width, height, board, showCheck } = this.props
        const rows = []
        for (let i=0; i<height; ++i) {
            const row = []
            for (let j=0; j<width; ++j) {
                row.push(board[(i*width)+j] === '.')
            }
            rows.push(row)
        }

        return (
            <div className='BoardBox'>
                <div className='Board'>
                    {rows.map((row, idx) => this.renderRow(row, idx))}
                </div>
                <MediaQuery maxWidth={1000}>
                    {this.renderCurrentClue()}
                </MediaQuery>
                {this._isMobile && (
                    <React.Fragment>
                        <Keyboard onKeyPress={this.onVirtualKeyDown} />
                    </React.Fragment>
                )}
                {showCheck && (
                    <Button
                        text={checking ? 'Uncheck' : 'Check'}
                        onClick={() => this.setState({ checking: !this.state.checking })} />
                )}
            </div>

        )
    }

    renderCurrentClue = () => {
        const { direction, clueSquare, selectedSquare, pingKeys } = this.state
        const { across, down, settings } = this.props
        const clues = { across, down }
        const { color } = settings
        const { row, col } = clueSquare
        let clue = ''

        if (!direction || !across || !down) {
            return null
        }

        Object.keys(clues[direction]).forEach(key => {
            const item = clues[direction][key]
            if (item.row === row && item.col === col) {
                clue = `${key}. ${item.clue}`
            }
        })

        const updatePingKey = () => {
            if (!selectedSquare || isNaN(selectedSquare.row) || isNaN(selectedSquare.col)) {
                return
            }
            const newPingKeys = [...pingKeys]
            newPingKeys[selectedSquare.row][selectedSquare.col] = `local-${shortId.generate()}`
            this.setState({ pingKeys: newPingKeys })
        }

        const classNames = ['CurrentClue']
        if (!this._isMobile) {
            classNames.push('desktop')
        }

        return (
            <div
                onClick={this.onClickClue}
                className={classNames.join(' ')}>
                <span>{clue}</span>
                <MobilePingButton onClick={updatePingKey} color={color} />
            </div>
        )
    }

    renderClues = (name, data) => {
        const { clueSquare, direction, finishedClues } = this.state
        if (!data) {
            return null
        }
        return (
            <MediaQuery minWidth={1000}>
                <div className='Clues'>
                    <h2>{name}</h2>
                    <ul>
                        {Object.keys(data).map(key => {
                            const val = data[key]
                            const onClick = () => {
                                const newSelectedSquare = { row: val.row, col: val.col }
                                this.updateSelectedSquare(newSelectedSquare)
                                this.setState({ direction: name })
                            }
                            const classNames = []
                            if (clueSquare.row === val.row && clueSquare.col === val.col && direction === name) {
                                classNames.push('selected')
                            }
                            if (finishedClues.has(key)) {
                                classNames.push('complete')
                            }
                            return (
                                <li key={`${name}-${key}`} className={classNames.join(' ')} onClick={onClick}>{key}. {val.clue}</li>
                            )
                        })}
                    </ul>
                </div>
            </MediaQuery>
        )
    }

    render() {
        const { showClues, across, down } = this.props
        return (
            <div className='Box'>
                <div className='Crossword'>
                    {/* <MediaQuery maxWidth={1000}>
                        <input
                            ref={ref => this._input = ref}
                            type='text'
                            autoComplete='off'
                            autoCorrect='off'
                            autoSuggest='off'
                            className='HiddenInput' />
                    </MediaQuery> */}
                    {showClues && this.renderClues('across', across)}
                    {this.renderBoard()}
                    {showClues && this.renderClues('down', down)}
                </div>
            </div>

        )
    }
}

export default Crossword
